import { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuickEditPanel({ photo, open, onOpenChange, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (photo) {
      setTitle(photo.title || photo.caption || "");
      setDescription(photo.description || "");
      setTags(photo.manual_tags || []);
      setTagInput("");
    }
  }, [photo]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((x) => [...x, t]);
    setTagInput("");
  };
  const onTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
    else if (e.key === "Backspace" && !tagInput && tags.length) setTags((x) => x.slice(0, -1));
  };
  const removeTag = (t) => setTags((x) => x.filter((v) => v !== t));

  const save = async () => {
    if (!photo) return;
    setSaving(true);
    try {
      const res = await api.post(`/photos/${photo.id}/edit`, {
        title: title.trim(),
        description: description.trim(),
        tags,
      });
      toast.success("Photo updated");
      onSaved?.(photo.id, res.data);
      onOpenChange(false);
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="quick-edit-panel">
        <SheetHeader className="border-b border-border p-5 text-left">
          <SheetTitle className="font-display">Quick edit</SheetTitle>
          <SheetDescription>Update the title, description and tags without leaving the library.</SheetDescription>
        </SheetHeader>

        {photo && (
          <div className="flex-1 overflow-y-auto p-5">
            <div className="mb-5 flex items-center gap-3">
              <img
                src={imageUrl(photo.id)}
                alt={photo.original_filename}
                className="h-16 w-16 rounded-md object-cover"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-zinc-800">{photo.original_filename}</p>
                <p className="text-xs text-muted-foreground">{photo.primary_domain || "Unclassified"}</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="qe-title">Title</Label>
                <Input
                  id="qe-title" data-testid="quick-edit-title"
                  value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give this photo a title" className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="qe-desc">Description</Label>
                <Textarea
                  id="qe-desc" data-testid="quick-edit-description"
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what's in this photo…" rows={5} className="mt-1.5 resize-none"
                />
              </div>

              <div>
                <Label htmlFor="qe-tags">Tags</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 rounded-md border border-border bg-white p-2 focus-within:ring-1 focus-within:ring-[#F27A54]">
                  {tags.map((t) => (
                    <span key={t} data-testid={`quick-edit-tag-${t}`}
                      className="flex items-center gap-1 rounded-sm bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#F27A54]">
                      {t}
                      <button type="button" onClick={() => removeTag(t)} className="hover:text-red-600">
                        <X size={11} weight="bold" />
                      </button>
                    </span>
                  ))}
                  <input
                    id="qe-tags" data-testid="quick-edit-tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={onTagKey}
                    onBlur={addTag}
                    placeholder={tags.length ? "" : "Type a tag and press Enter"}
                    className="min-w-[120px] flex-1 border-none bg-transparent text-sm outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Press Enter or comma to add a tag.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border p-5">
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            data-testid="quick-edit-save" onClick={save} disabled={saving}
            className="flex items-center gap-1.5 rounded-md bg-[#F27A54] px-4 py-2 text-sm font-semibold text-white hover:bg-[#00268a] disabled:opacity-60"
          >
            <FloppyDisk size={15} weight="bold" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
